import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { ExtraMessageWithBlurredBg } from '@sorare/core/src/components/onboarding/ExtraMessageWithBlurredBg';
import { HighlightOverlay } from '@sorare/core/src/components/onboarding/HighlightOverlay';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';

import { MVPBenchOnboarding } from '#src/components/onboarding/MVPBenchOnboarding/index.tsx';

type Props = {
  skipScoreDetailsOnboarding?: boolean;
};
export const NBABenchOnboarding = ({ skipScoreDetailsOnboarding }: Props) => {
  const { sport } = useSportContext();
  const [step, setStep] = useState(0);
  const isTabletAndAbove = useIsTabletAndAbove();
  const { lifecycle, update: updateLifecycle } = useLifecycle();

  if (sport !== Sport.NBA) {
    return null;
  }

  if (lifecycle?.sawComposeTeamHighlights) {
    return <MVPBenchOnboarding />;
  }

  if (step === 0 && !skipScoreDetailsOnboarding) {
    return (
      <HighlightOverlay
        key="ComposeTeamBenchCardFooter"
        highlightNames={['ComposeTeamBenchCardFooter']}
        onClose={() => setStep(1)}
        extras={[
          {
            nextTo: 'ComposeTeamBenchCardFooter',
            render: props => (
              <ExtraMessageWithBlurredBg
                align="center"
                anchor="bottom"
                positionProps={props}
                fullWidth={!isTabletAndAbove}
                buttonProps={{
                  onClick: () => setStep(1),
                  children: <FormattedMessage {...glossary.continue} />,
                  color: 'primary',
                }}
              >
                <LabelL>
                  <FormattedMessage defaultMessage="Tapping the bottom bar shows a breakdown of a player’s previous performances" />
                </LabelL>
              </ExtraMessageWithBlurredBg>
            ),
          },
        ]}
      />
    );
  }

  return (
    <HighlightOverlay
      highlightNames={['ComposeTeamRemainingPoints']}
      onClose={async () =>
        updateLifecycle(LIFECYCLE.sawComposeTeamHighlights, true)
      }
      extras={[
        {
          nextTo: 'ComposeTeamRemainingPoints',
          render: props => (
            <ExtraMessageWithBlurredBg
              align="start"
              anchor="top"
              positionProps={props}
              fullWidth={!isTabletAndAbove}
              buttonProps={{
                onClick: async () =>
                  updateLifecycle(LIFECYCLE.sawComposeTeamHighlights, true),
                children: (
                  <FormattedMessage defaultMessage="Compose my Lineup" />
                ),
                color: 'primary',
              }}
            >
              <LabelL>
                <FormattedMessage defaultMessage="Their L10 count against the Points Cap. Keep your lineup within the cap!" />
              </LabelL>
            </ExtraMessageWithBlurredBg>
          ),
        },
      ]}
    />
  );
};
